import { CategoryData } from './residentialCategories'
import { residentialCategories } from './residentialCategories'
import { commercialCategories } from './commercialCategories'
import { signsAccentsCategories } from './signsAccentsCategories'

export type CategoryContext = 'Residential' | 'Commercial' | 'Signs & Accents'

export type CategoryRoute = {
  route: string
  label: string
  context: CategoryContext
}

/**
 * Authoritative list of every visible category/listing route. These are
 * recognized as category pages BEFORE the generic legacy fallback, even when
 * there is no matching sample product data yet. Labels here are only used as
 * the H1 when a route has no product-data entry; routes that DO have product
 * data reuse that entry's label/products.
 */
export const CATEGORY_SEO_PATHS: CategoryRoute[] = [
  // Mailboxes (Residential)
  { route: '/6m8/mailboxes.html', label: 'Mailboxes', context: 'Residential' },
  {
    route: '/6m8/curbside-mailboxes.html',
    label: 'Curbside Mailboxes',
    context: 'Residential',
  },
  {
    route: '/6m8/mailbox-posts.html',
    label: 'Mailbox Posts',
    context: 'Residential',
  },
  {
    route: '/6m8/mailboxes/complete-mailbox-post-system.html',
    label: 'Complete Mailbox & Post Systems',
    context: 'Residential',
  },
  {
    route: '/6m8/mailboxes/wall-mount-mailboxes.html',
    label: 'Wall Mount Mailboxes',
    context: 'Residential',
  },
  {
    route: '/6m8/mailboxes/locking-mailboxes.html',
    label: 'Locking Mailboxes',
    context: 'Residential',
  },
  {
    route: '/6m8/mailboxes/stone-brick-column-mailboxes.html',
    label: 'Stone & Brick Column Mailboxes',
    context: 'Residential',
  },
  {
    route: '/6m8/mailboxes/mailbox-replacement-parts-and-accessories.html',
    label: 'Mailbox Replacement Parts',
    context: 'Residential',
  },
  {
    route: '/6m8/mailboxes/door-chimes-doorbells.html',
    label: 'Door Chimes & Doorbells',
    context: 'Residential',
  },
  {
    route: '/6m8/mailboxes/replacement-numbers.html',
    label: 'Replacement Numbers',
    context: 'Residential',
  },

  // Commercial
  {
    route: '/6m8/commercial-mailboxes-lockers.html',
    label: 'Commercial Mailboxes & Lockers',
    context: 'Commercial',
  },
  {
    route:
      '/6m8/commercial-mailboxes-lockers/centralized-delivery-mailboxes-and-kiosks.html',
    label: 'Centralized Delivery Mailboxes & Kiosks',
    context: 'Commercial',
  },
  {
    route: '/6m8/commercial-mailboxes-lockers/private-delivery-mailboxes.html',
    label: 'Private Delivery Mailboxes',
    context: 'Commercial',
  },
  {
    route: '/6m8/commercial-mailboxes-lockers/parcel-lockers.html',
    label: 'Parcel Lockers',
    context: 'Commercial',
  },
  {
    route:
      '/6m8/commercial-mailboxes-lockers/mail-slots-letter-book-drops.html',
    label: 'Mail Slots, Letter & Book Drops',
    context: 'Commercial',
  },
  {
    route: '/6m8/commercial-mailboxes-lockers/collection-boxes.html',
    label: 'Collection Boxes',
    context: 'Commercial',
  },
  {
    route: '/6m8/commercial-mailboxes-lockers/key-keepers-key-blanks.html',
    label: 'Key Keepers & Key Blanks',
    context: 'Commercial',
  },
  {
    route:
      '/6m8/commercial-mailboxes-lockers/replacement-commercial-locks-hinges-doors.html',
    label: 'Commercial Replacement Locks, Hinges & Doors',
    context: 'Commercial',
  },

  // Street Signs (Signs & Accents)
  {
    route: '/6m8/street-signs-traffic-signs.html',
    label: 'Street Signs & Traffic Signs',
    context: 'Signs & Accents',
  },
  {
    route: '/6m8/street-signs-traffic-signs/traffic-road-signs.html',
    label: 'Traffic & Road Signs',
    context: 'Signs & Accents',
  },
  {
    route: '/6m8/street-signs-traffic-signs/sign-frames-back-plates.html',
    label: 'Sign Frames & Back Plates',
    context: 'Signs & Accents',
  },

  // Decorative Signage
  {
    route: '/6m8/decorative-signage.html',
    label: 'Decorative Signage',
    context: 'Signs & Accents',
  },
  {
    route: '/6m8/decorative-signage/decorative-street-sign.html',
    label: 'Decorative Street Signs',
    context: 'Signs & Accents',
  },
  {
    route: '/6m8/decorative-signage/decorative-stop-signs.html',
    label: 'Decorative Stop Signs',
    context: 'Signs & Accents',
  },
  {
    route: '/6m8/decorative-signage/decorative-traffic-signage.html',
    label: 'Decorative Traffic Signage',
    context: 'Signs & Accents',
  },
  {
    route: '/6m8/decorative-signage/replacement-bases-finials-sign-mounts.html',
    label: 'Replacement Bases, Finials & Sign Mounts',
    context: 'Signs & Accents',
  },
  {
    route: '/6m8/decorative-signage/sign-frames-back-plates.html',
    label: 'Sign Frames & Back Plates',
    context: 'Signs & Accents',
  },

  // Message Boards
  {
    route: '/6m8/message-boards.html',
    label: 'Message Boards',
    context: 'Signs & Accents',
  },

  // Address Plaques
  {
    route: '/6m8/address-plaques-and-house-numbers.html',
    label: 'Address Plaques & House Numbers',
    context: 'Signs & Accents',
  },

  // Home & Garden Accents
  {
    route: '/6m8/home-garden-accents.html',
    label: 'Home & Garden Accents',
    context: 'Signs & Accents',
  },
  {
    route: '/6m8/home-garden-accents/home-accents.html',
    label: 'Home Accents',
    context: 'Signs & Accents',
  },
  {
    route: '/6m8/home-garden-accents/fairfield-50qt-coolers.html',
    label: 'Fairfield 50qt Coolers',
    context: 'Signs & Accents',
  },
  {
    route: '/6m8/home-garden-accents/planters.html',
    label: 'Planters',
    context: 'Signs & Accents',
  },
  {
    route: '/6m8/home-garden-accents/window-box-planters.html',
    label: 'Window Box Planters',
    context: 'Signs & Accents',
  },
  {
    route: '/6m8/home-garden-accents/door-mats.html',
    label: 'Door Mats',
    context: 'Signs & Accents',
  },

  // Lighting
  {
    route: '/6m8/lighting.html',
    label: 'Lighting',
    context: 'Signs & Accents',
  },
  {
    route: '/6m8/lighting/outdoor-lamp-posts.html',
    label: 'Outdoor Lamp Posts',
    context: 'Signs & Accents',
  },
  {
    route: '/6m8/lighting/outdoor-exterior-lighting.html',
    label: 'Outdoor & Exterior Lighting',
    context: 'Signs & Accents',
  },
  {
    route: '/6m8/lighting/exterior-home-lighting.html',
    label: 'Exterior Home Lighting',
    context: 'Signs & Accents',
  },
  {
    route: '/6m8/lighting/commercial-lighting.html',
    label: 'Commercial Lighting',
    context: 'Signs & Accents',
  },
  {
    route: '/6m8/lighting/light-blocks.html',
    label: 'Light Blocks',
    context: 'Signs & Accents',
  },
]

const PRODUCT_DATA_BY_ROUTE: Record<string, CategoryData> = {}
for (const c of [
  ...residentialCategories,
  ...commercialCategories,
  ...signsAccentsCategories,
]) {
  PRODUCT_DATA_BY_ROUTE[c.route] = c
}

/**
 * Resolve a category route to renderable CategoryData. If the route has sample
 * product data, that entry is returned (preserving products). Otherwise a
 * polished empty category is returned so the page still renders with the SEO
 * description and a product-help state instead of the legacy placeholder.
 */
export function getCategoryForRoute(entry: CategoryRoute): CategoryData {
  const existing = PRODUCT_DATA_BY_ROUTE[entry.route]
  if (existing) return existing
  return {
    route: entry.route,
    label: entry.label,
    sourceUrl: `https://www.midatlanticmailboxes.com${entry.route}`,
    total: 0,
    products: [],
  }
}
